import { InputGroup } from '@/components/ui/input-group';
import { Box, Button, HStack, Icon, Input, Skeleton, Text, VStack } from '@chakra-ui/react';
import type { MentionBoostCampaignDto } from '@fluenceur/types';
import { Folder, InfoCircle, Search } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PlacementCampaignRow } from './PlacementCampaignRow';

interface PlacementCampaignListProps {
  campaigns: MentionBoostCampaignDto[];
  isLoading: boolean;
  hiddenCount: number;
  // The account has more campaigns than the read ceiling: say so, and point at
  // the search, which runs over the whole account.
  isTruncated: boolean;
  search: string;
  hasSearch: boolean;
  selectedCampaignId: string | null;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onSelect: (campaign: MentionBoostCampaignDto) => void;
  // Returns to the default placement from the empty state.
  onBackToNewCampaign: () => void;
}

/** Search box + the compatible campaigns, or the relevant empty state. */
export function PlacementCampaignList({
  campaigns,
  isLoading,
  hiddenCount,
  isTruncated,
  search,
  hasSearch,
  selectedCampaignId,
  onSearchChange,
  onClearSearch,
  onSelect,
  onBackToNewCampaign,
}: PlacementCampaignListProps) {
  const { t } = useTranslation();

  return (
    <VStack align="stretch" gap={2}>
      <InputGroup
        startElement={
          <Icon size="sm" color="fg.muted">
            <Search />
          </Icon>
        }
      >
        <Input
          variant="outline"
          size="sm"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('mentions.boost.setup.placement.searchPlaceholder')}
        />
      </InputGroup>

      {isLoading ? (
        <VStack align="stretch" gap={2}>
          <Skeleton h="52px" rounded="lg" />
          <Skeleton h="52px" rounded="lg" />
        </VStack>
      ) : campaigns.length === 0 ? (
        // A search with no match is NOT "you have no compatible campaign": say
        // which one it is, and never offer to drop the selection from here.
        hasSearch ? (
          <PlacementSearchEmptyState onClearSearch={onClearSearch} />
        ) : (
          <PlacementEmptyState onBackToNewCampaign={onBackToNewCampaign} />
        )
      ) : (
        <VStack align="stretch" gap={2}>
          {campaigns.map((campaign) => (
            <PlacementCampaignRow
              key={campaign.id}
              campaign={campaign}
              isSelected={campaign.id === selectedCampaignId}
              onSelect={onSelect}
            />
          ))}
        </VStack>
      )}

      {!isLoading && isTruncated && (
        <PlacementNote>{t('mentions.boost.setup.placement.truncatedNote')}</PlacementNote>
      )}

      {!isLoading && hiddenCount > 0 && (
        <PlacementNote>
          {t('mentions.boost.setup.placement.hiddenNote', { count: hiddenCount })}
        </PlacementNote>
      )}
    </VStack>
  );
}

/** Small informational line under the list. */
function PlacementNote({ children }: { children: ReactNode }) {
  return (
    <HStack gap={1.5} align="flex-start">
      <Icon size="xs" color="fg.muted" mt={0.5}>
        <InfoCircle />
      </Icon>
      <Text fontSize="xs" color="fg.muted">
        {children}
      </Text>
    </HStack>
  );
}

/** No campaign survives the compatibility filter: explain, and offer the way back. */
function PlacementEmptyState({ onBackToNewCampaign }: { onBackToNewCampaign: () => void }) {
  const { t } = useTranslation();

  return (
    <Box border="1px solid" borderColor="border.muted" rounded="lg" px={4} py={5}>
      <VStack gap={2}>
        <Box bg="bg.subtle" rounded="full" p={2.5}>
          <Icon size="sm" color="fg.muted">
            <Folder />
          </Icon>
        </Box>
        <Text fontSize="sm" fontWeight="semibold">
          {t('mentions.boost.setup.placement.emptyTitle')}
        </Text>
        <Text fontSize="xs" color="fg.muted" textAlign="center">
          {t('mentions.boost.setup.placement.emptyDescription')}
        </Text>
        <Button size="xs" variant="outline" rounded="full" onClick={onBackToNewCampaign}>
          {t('mentions.boost.setup.placement.emptyCta')}
        </Button>
      </VStack>
    </Box>
  );
}

/**
 * The search matched nothing. Deliberately distinct from the empty state: the
 * brand DOES have compatible campaigns, and the only sensible action is to
 * clear the search — offering to drop the placement here would silently destroy
 * a selection the brand never asked to change.
 */
function PlacementSearchEmptyState({ onClearSearch }: { onClearSearch: () => void }) {
  const { t } = useTranslation();

  return (
    <Box border="1px solid" borderColor="border.muted" rounded="lg" px={4} py={5}>
      <VStack gap={2}>
        <Text fontSize="sm" fontWeight="semibold">
          {t('mentions.boost.setup.placement.searchEmptyTitle')}
        </Text>
        <Text fontSize="xs" color="fg.muted" textAlign="center">
          {t('mentions.boost.setup.placement.searchEmptyDescription')}
        </Text>
        <Button size="xs" variant="outline" rounded="full" onClick={onClearSearch}>
          {t('mentions.boost.setup.placement.clearSearchCta')}
        </Button>
      </VStack>
    </Box>
  );
}
